/**
 * Pure pagination state machine.
 *
 * Lives outside the React hook so the transition logic can be unit tested
 * under `node --test` without a renderer. The hook in `usePaginatedFetcher.ts`
 * is a thin wrapper that drives an effect off this reducer.
 *
 * State shape:
 *   - `pages`: every Page<T> we've successfully loaded, in fetch order.
 *   - `loading`: a fetch is currently in flight.
 *   - `error`: last fetch failed; we keep prior `pages` so the user doesn't
 *     lose context. Cleared on success or `retry`.
 *   - `inFlightPage`: the page number we asked for last; null when idle.
 *     Used to defend against duplicate-load dispatches before a response
 *     comes back.
 *
 * Invariants enforced by `paginatedReducer` and asserted by tests:
 *   - `request` is a no-op when already `loading`.
 *   - `request` is a no-op when `error` is set (caller must `retry` first).
 *   - `request` is a no-op when `hasMore(state)` is false (after at least
 *     one successful fetch).
 *   - `request` is a no-op once we've hit `MAX_PAGES`.
 *   - `success` mutates `pages` only if `inFlightPage === page.page`, so a
 *     stale response (e.g. resetKey changed mid-flight) is dropped.
 */
import type { Page } from '@/sources/types';

/**
 * Hard safety cap on the number of pages a single mounted fetcher can ever
 * load. Sources can erroneously report `hasMore: true` indefinitely. Capping
 * at 100 prevents a runaway scroll from melting the user's RAM.
 */
export const MAX_PAGES = 100;

export interface PaginatedState<T> {
  pages: Page<T>[];
  loading: boolean;
  error: unknown | null;
  inFlightPage: number | null;
  inFlightCursor: string | null;
}

export type PaginatedAction<T> =
  /** Caller wants to fetch the next page (or page 1 for an empty state). */
  | { type: 'request'; page: number; cursor?: string }
  /** A fetch completed successfully. */
  | { type: 'success'; page: Page<T> }
  /** A fetch failed. */
  | { type: 'failure'; error: unknown; page: number }
  /** Clear `error` so the next `request` can proceed. */
  | { type: 'retry' }
  /** Restore previously loaded pages for the same route/search key. */
  | { type: 'restore'; pages: Page<T>[] }
  /** Wipe all state (used when `resetKey` changes). */
  | { type: 'reset' };

export function initialState<T>(): PaginatedState<T> {
  return { pages: [], loading: false, error: null, inFlightPage: null, inFlightCursor: null };
}

/**
 * Pure reducer. Identity-stable on no-op transitions so React rerenders are
 * minimized when callsites dispatch redundant `request` actions.
 */
export function paginatedReducer<T>(
  state: PaginatedState<T>,
  action: PaginatedAction<T>,
): PaginatedState<T> {
  switch (action.type) {
    case 'request': {
      if (state.loading) return state;
      if (state.error !== null) return state;
      if (state.pages.length >= MAX_PAGES) return state;
      // Don't re-request a page we already have. Callsites compute
      // `nextPageNumber(state)` so this is mostly defensive.
      if (state.pages.some((p) => p.page === action.page)) return state;
      // If there's at least one successful page and it says no more, refuse.
      const last = state.pages[state.pages.length - 1];
      if (last && !last.hasMore) return state;
      return {
        ...state,
        loading: true,
        inFlightPage: action.page,
        inFlightCursor: action.cursor ?? null,
      };
    }
    case 'success': {
      // Drop stale responses: if the page number doesn't match what we
      // asked for (e.g. `reset` fired mid-flight, or two requests raced),
      // ignore it.
      if (state.inFlightPage !== action.page.page) return state;
      return {
        ...state,
        pages: [...state.pages, action.page],
        loading: false,
        error: null,
        inFlightPage: null,
        inFlightCursor: null,
      };
    }
    case 'failure': {
      if (state.inFlightPage !== action.page) return state;
      return {
        ...state,
        loading: false,
        error: action.error,
        inFlightPage: null,
        inFlightCursor: null,
      };
    }
    case 'retry': {
      if (state.error === null) return state;
      return { ...state, error: null };
    }
    case 'restore': {
      return {
        pages: action.pages,
        loading: false,
        error: null,
        inFlightPage: null,
        inFlightCursor: null,
      };
    }
    case 'reset': {
      return initialState<T>();
    }
    default:
      return state;
  }
}

/** Flat list of every item across all loaded pages, in fetch order. */
export function flattenItems<T>(state: PaginatedState<T>): T[] {
  const out: T[] = [];
  for (const p of state.pages) {
    for (const it of p.items) out.push(it);
  }
  return out;
}

/**
 * Whether the caller should expose a "Load more" affordance.
 *
 * - Before any successful fetch: true (we'll fetch page 1).
 * - After at least one page: defer to the source via the last page's `hasMore`.
 * - Suppressed while a fetch is in flight (we don't double-trigger).
 * - Suppressed while an error is pending (user must `retry` first).
 * - Suppressed at the MAX_PAGES cap.
 */
export function hasMore<T>(state: PaginatedState<T>): boolean {
  if (state.loading) return false;
  if (state.error !== null) return false;
  if (state.pages.length >= MAX_PAGES) return false;
  if (state.pages.length === 0) return true;
  return state.pages[state.pages.length - 1]!.hasMore === true;
}

/**
 * Next page number to request. 1 when empty; otherwise `lastPage.page + 1`.
 * Sources are 1-indexed by convention (see `Page<T>` in `src/sources/types.ts`).
 */
export function nextPageNumber<T>(state: PaginatedState<T>): number {
  if (state.pages.length === 0) return 1;
  return state.pages[state.pages.length - 1]!.page + 1;
}
